import React, {useState} from "react";

import {useGetPaginatedDatasets} from "api/api-hooks/datasets-hooks";
import {DatasetInfo} from "api/model/dataset/DatasetInfo";
import PaginatorWithoutTotalCount from "components/basic/PaginatorWithoutTotalCount";
import {getSearchParams, mergeUniqueObjects} from "components/dashboard/dataset-window/utils/utils";
import useDebounce from "hooks/useDebounce";
import {useHasPermissions} from "hooks/useHasPermissions";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Divider} from "primereact/divider";
import {InputText} from "primereact/inputtext";
import {Tag} from "primereact/tag";
import {Tooltip} from "react-tooltip";
import useCurrentCompanyContext from "../../../hooks/useCurrentCompanyContext";

const DatasetSelection = ({datasets, setDatasets}:
                              {datasets: Array<DatasetInfo>, setDatasets: (arr: Array<DatasetInfo>) => void}) => {
    const [searchBar, setSearchBar] = useState("");
    const [lazyParams, setLazyParams] = useState({page: 0, rows: 30});
    const isSuperUser = useHasPermissions('SUPER_USER')
    const companyCtx = useCurrentCompanyContext()

    const searchValue = useDebounce<string>(searchBar, {
      delay: 400,
      callbackOnTime: () => {
        setLazyParams(old => ({ ...old, page: 0 }));
      },
    });
    
    const {
        data,
        isLoading,
        isError,
    } = useGetPaginatedDatasets({...getSearchParams(searchValue, lazyParams), companyRef: companyCtx?.companyReference }, {refetchOnWindowFocus: false});

    const handleDatasetSelection = ({originalEvent, value}: {value: any, originalEvent: any}) => {
            if (originalEvent && originalEvent.altKey) {
            setDatasets(value);
            return;
        }
        if (originalEvent && originalEvent.shiftKey) {
            const uniqueSelection = mergeUniqueObjects(datasets, value);
            setDatasets([...uniqueSelection]);
        } else if (datasets.some((dataset) => dataset.identifier === value[0].identifier)) {
                setDatasets([...datasets.filter(obj => obj.identifier !== value[0].identifier)]);
            } else {
                setDatasets([...datasets, ...value]);
            }

    };

    const rowClass = (rowData: DatasetInfo) => ({"row-selected": datasets.find((s) => s.identifier === rowData.identifier)});

    if (isError) {
        return <div>Error while fetching datasets...</div>;
    }

    const titleBodyTemplate = (rowData: DatasetInfo) => {
        const datasetClassAbbreviated = rowData.datasetClass?.split("_").map((s: string) => s[0]).join("");
        return (
            <span className="flex align-items-center">
                {isSuperUser && <>
                    <Tooltip id="dataset-class" opacity={0.5} offset={0.5}/>
                    <Tag
                        data-tooltip-id="dataset-class"
                        className="text-black-alpha-50 bg-transparent"
                        value={datasetClassAbbreviated}
                        data-tooltip-content={rowData.datasetClass}
                    />
                </>}
                {rowData.title}
            </span>
        );
    };

    return <div className="selectionPanel fullWidth">
        <div style={{position: "relative"}}>
            <i className="pi pi-search"
               style={{position: "absolute", top: "11px", left: "5px", "fontSize": "1.4em"}}/>
            {searchValue && <i className="pi pi-times"
                               style={{
                                   cursor: "pointer",
                                   position: "absolute",
                                   top: "11px",
                                   right: "5px",
                                   "fontSize": "1.4em"
                               }}
                               onClick={() => setSearchBar("")}
            />}
            <InputText
                value={searchBar}
                onChange={(e) => setSearchBar(e.target.value)}
                placeholder="Search"
                style={{width: "100%", paddingLeft: "2.2rem"}}

            />
        </div>
        <Divider/>
        <div style={{flexGrow: 1}}>
            <DataTable
                value={data?.map((pd: DatasetInfo) => ({...pd, formatedTitle: pd.toString()}))}
                lazy
                selectionMode="multiple"
                onSelectionChange={handleDatasetSelection}
                dataKey="identifier"
                responsiveLayout="scroll"
                className="text row fullWidth"
                rowClassName={rowClass}
                emptyMessage="No datasets found."
                showGridlines
                loading={isLoading}
            >
                <Column
                    key="dataset"
                    field="title"
                    header="Dataset"
                    className="row"
                    style={{width: "30rem"}}
                    body={titleBodyTemplate}
                />
                <Column
                    key="unit"
                    field="unit"
                    header="Unit"
                    className="row"
                    style={{width: "10px", whiteSpace: "nowrap"}}
                />
            </DataTable>
        </div>
        <PaginatorWithoutTotalCount
            page={lazyParams.page}
            maxRows={lazyParams.rows}
            rows={data?.length}
            setPage={(page) => setLazyParams(old => ({...old, page}))}
        />
    </div>;
};


export default DatasetSelection;